import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import {
    PersonalProductRecommendationBuilder,
    PopularProductsBuilder,
    ProductsViewedAfterViewingProductBuilder,
    PurchasedWithProductBuilder,
    RecentlyViewedProductsBuilder,
    Recommender,
    SimilarProductsProductBuilder,
    ValueSelectorFactory
} from '@relewise/client';
import { RelewiseMappingService } from './MappingService';
import { RelewiseProductResult } from '../types/relewiseData';
import { DisplayedAtLocation, MappedProduct } from '../types/mapped';
import { BaseRelewiseService, InitializedRelewiseService } from './BaseRelewiseService/BaseRelewiseService';

const POPULARITY_TIME_WINDOW = 10080; // 7 days in minutes
const RELEVANCE_MODIFIER_STRENGTH_DIVIDER = 10;
const DEFAULT_RELEVANCE_MODIFIER_STRENGTH = 20;
const MAX_RELEVANCE_MODIFIER_STRENGTH = 100;
const MIN_RELEVANCE_MODIFIER_STRENGTH = 10;
const BY_PRODUCT_RECOMMENDATION_TYPES = ['similar', 'purchasedWith', 'viewedAfter'];

export type ProductRecommendationTypes =
    | 'mostViewed'
    | 'mostPurchased'
    | 'recentlyViewed'
    | 'personalized'
    | 'purchasedWith'
    | 'similar'
    | 'viewedAfter';
export type RelevanceModifier = 'boostDiscounted' | 'none';

interface GenericProductRecommendationParams {
    recommendationType: ProductRecommendationTypes;
    count: number;
    displayLocation: DisplayedAtLocation;
    categoryId?: string;
    masterProductItemId?: string;
    relevanceModifier?: RelevanceModifier;
    relevanceModifierStrength?: number;
}

interface InitializedService extends InitializedRelewiseService {
    recommender: Recommender;
    currencyId: string;
    locale: string;
    legalEntityId: string;
    environmentName: string;
    relewiseMappingService: RelewiseMappingService;
    disabled: false;
}

export class RelewiseRecommendationService extends BaseRelewiseService<InitializedService> {
    protected recommender: Recommender | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Recommendation Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.recommender = new Recommender(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });
    }

    private checkIfByProductRecommendationType(recommendationType: ProductRecommendationTypes) {
        return BY_PRODUCT_RECOMMENDATION_TYPES.includes(recommendationType);
    }

    public async getProductRecommendations({
        recommendationType,
        count,
        displayLocation,
        categoryId,
        masterProductItemId,
        relevanceModifier,
        relevanceModifierStrength
    }: GenericProductRecommendationParams): Promise<MappedProduct[] | undefined> {
        if (!this.isEnabled()) {
            return [];
        }

        const isByProductRecommendation = this.checkIfByProductRecommendationType(recommendationType);

        try {
            if (isByProductRecommendation && !masterProductItemId) {
                throw new Error(`Recommendation of type ${recommendationType} requires a masterProductItemId.`);
            }

            const settings = await this.getApiRequestSettings(displayLocation);

            let builder;
            if (recommendationType === 'personalized') {
                builder = new PersonalProductRecommendationBuilder(settings);
            }
            if (recommendationType === 'mostPurchased') {
                builder = new PopularProductsBuilder(settings).basedOn('MostPurchased').sinceMinutesAgo(POPULARITY_TIME_WINDOW);
            }
            if (recommendationType === 'mostViewed') {
                builder = new PopularProductsBuilder(settings).basedOn('MostViewed').sinceMinutesAgo(POPULARITY_TIME_WINDOW);
            }
            if (recommendationType === 'recentlyViewed') {
                builder = new RecentlyViewedProductsBuilder(settings);
            }
            if (isByProductRecommendation && masterProductItemId) {
                const productId = this.relewiseMappingService?.prefixId(masterProductItemId);
                if (recommendationType === 'similar') {
                    builder = new SimilarProductsProductBuilder(settings).product({
                        productId: productId
                    });
                }
                if (recommendationType === 'purchasedWith') {
                    builder = new PurchasedWithProductBuilder(settings).product({
                        productId: productId
                    });
                }
                if (recommendationType === 'viewedAfter') {
                    builder = new ProductsViewedAfterViewingProductBuilder(settings).product({
                        productId: productId
                    });
                }
            }

            if (!builder) {
                throw new Error(`Unsupported recommendation type: ${recommendationType}`);
            }

            builder
                .setNumberOfRecommendations(count)
                .allowFillIfNecessaryToReachNumberOfRecommendations(true)
                .allowProductsCurrentlyInCart(false)
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .filters(f => {
                    this.addProductEnvironmentFilter(f);
                    if (categoryId && this.relewiseMappingService) {
                        f.addProductCategoryIdFilter('Ancestor', this.relewiseMappingService.prefixId(categoryId));
                    }
                    this.addProductAndVariantStockFilters(f);
                });

            if (relevanceModifier === 'boostDiscounted') {
                const cappedRelevanceModifierStrength = Math.min(
                    Math.max(relevanceModifierStrength ?? DEFAULT_RELEVANCE_MODIFIER_STRENGTH, MIN_RELEVANCE_MODIFIER_STRENGTH),
                    MAX_RELEVANCE_MODIFIER_STRENGTH
                );
                const adjustedModifierStrength = cappedRelevanceModifierStrength / RELEVANCE_MODIFIER_STRENGTH_DIVIDER;
                builder.relevanceModifiers(m => {
                    m.addProductDataRelevanceModifier(
                        `HasDiscountedVariant_${this.legalEntityId}`,
                        b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                        ValueSelectorFactory.fixedDoubleValueSelector(adjustedModifierStrength),
                        true,
                        false
                    );
                    m.addVariantDataRelevanceModifier(
                        `IsDiscounted_${this.legalEntityId}`,
                        b => {
                            b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false });
                        },
                        ValueSelectorFactory.fixedDoubleValueSelector(adjustedModifierStrength),
                        true,
                        false
                    );
                });
            }

            let result;
            if (builder instanceof PersonalProductRecommendationBuilder) {
                result = await this.recommender.recommendPersonalProducts(builder.build());
            }
            if (builder instanceof PopularProductsBuilder) {
                result = await this.recommender.recommendPopularProducts(builder.build());
            }
            if (builder instanceof RecentlyViewedProductsBuilder) {
                result = await this.recommender.recentlyViewedProducts(builder.build());
            }
            if (builder instanceof SimilarProductsProductBuilder) {
                result = await this.recommender.recommendSimilarProducts(builder.build());
            }
            if (builder instanceof PurchasedWithProductBuilder) {
                result = await this.recommender.recommendPurchasedWithProduct(builder.build());
            }
            if (builder instanceof ProductsViewedAfterViewingProductBuilder) {
                result = await this.recommender.recommendProductsViewedAfterViewingProduct(builder.build());
            }

            const mappedProducts = this.relewiseMappingService.mapRelewiseProductResultList(
                result?.recommendations as RelewiseProductResult[]
            );

            return mappedProducts;
        } catch (err) {
            this.logError(`getProductRecommendations request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }
}
